import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';
import { calculateStats } from '../gameLogic/dragonLogic.js';
import { getRarityColor, getElementGlow } from '../utils/gameUtils.js';
import { getAssetKey } from '../utils/assetUtils.js';

const StatPill = ({ statKey, value }) => (
  <div className="bg-black/30 rounded-full px-2 py-1 flex items-center justify-center text-xs font-bold min-w-0">
    <span className="mr-1 text-xs">{CONSTANTS.BATTLE_STAT_ICONS[statKey]}</span>
    <span className="truncate">{value}</span>
  </div>
);

const BattleDragonCard = ({ dragon, isOpponent, isUsed, isRevealed, isSelected, onClick }) => {
  if (!isRevealed) {
    return (
      <div className="battle-card w-36 h-52 bg-gray-800/50 border-2 border-purple-500/30 rounded-lg flex items-center justify-center shadow-lg">
        <img src={ASSETS.cards.cardBack.path} alt="Card Back" className="w-28 h-auto" />
      </div>
    );
  }

  const dragonData = CONSTANTS.DRAGONS[dragon.name];
  const stats = calculateStats(dragon);

  return (
    <div
      className={`battle-card w-36 h-52 bg-gray-900/70 border-2 rounded-lg p-2 flex flex-col text-white shadow-md transition-all duration-300 ${
        isUsed ? 'used' : ''
      } ${!isOpponent && !isUsed ? 'player-hand' : ''} ${isSelected ? 'selected border-yellow-400' : 'border-transparent'}`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="text-center mb-1">
        <p className="text-sm font-bold truncate font-mono" title={dragon.name}>{dragon.name}</p>
        <p className="text-xs" style={{ color: getRarityColor(dragonData.rarity) }}>
          Lvl {dragon.level}
        </p>
      </div>

      {/* Dragon Image */}
      <div className="flex-shrink-0 flex justify-center mb-2">
        <img
          src={ASSETS.dragons[getAssetKey(dragon.name)].path}
          alt={dragon.name}
          className="w-16 h-16"
          style={{ filter: `drop-shadow(0 0 5px ${getElementGlow(dragonData.element)})` }}
        />
      </div>

      {/* Stats Grid - 2x3 layout */}
      <div className="flex-1 grid grid-cols-2 gap-1 text-xs font-sans">
        <StatPill statKey="attack" value={stats.attack} />
        <StatPill statKey="speed" value={stats.speed} />
        <StatPill statKey="defense" value={stats.defense} />
        <StatPill statKey="strength" value={stats.strength} />
        <StatPill statKey="physique" value={stats.physique} />
        <StatPill statKey="hiddenPower" value={stats.hiddenPower} />
      </div>
    </div>
  );
};

const CountingNumber = ({ targetValue, duration, onComplete }) => {
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const newValue = Math.floor(easeOutQuart * targetValue);
      
      setCurrentValue(newValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(targetValue);
        if (onComplete) onComplete();
      }
    };
    
    requestAnimationFrame(animate);
  }, [targetValue, duration, onComplete]);

  return <span>{currentValue}</span>;
};

const FaceOffAnimation = ({ faceOffData, onAnimationEnd, onSkip }) => {
  const { playerDragon, opponentDragon, stat, result, playerStat, opponentStat, tieBreaker, playerTotalStats, opponentTotalStats } = faceOffData;
  const [phase, setPhase] = useState('moving'); // moving, counting, clashing, overlay, result
  const [showSkip, setShowSkip] = useState(true);

  const dragonData1 = CONSTANTS.DRAGONS[playerDragon.name];
  const dragonData2 = CONSTANTS.DRAGONS[opponentDragon.name];

  useEffect(() => {
    const timer1 = setTimeout(() => setPhase('counting'), CONSTANTS.FACE_OFF_CARD_MOVE_DURATION);
    const timer2 = setTimeout(() => setPhase('clashing'), CONSTANTS.FACE_OFF_CARD_MOVE_DURATION + CONSTANTS.FACE_OFF_STAT_COUNT_DURATION);
    const timer3 = setTimeout(() => setPhase('overlay'), CONSTANTS.FACE_OFF_CARD_MOVE_DURATION + CONSTANTS.FACE_OFF_STAT_COUNT_DURATION + CONSTANTS.FACE_OFF_CLASH_DURATION);
    const timer4 = setTimeout(() => setPhase('result'), CONSTANTS.FACE_OFF_CARD_MOVE_DURATION + CONSTANTS.FACE_OFF_STAT_COUNT_DURATION + CONSTANTS.FACE_OFF_CLASH_DURATION + CONSTANTS.FACE_OFF_RESULT_OVERLAY_DURATION);
    const timer5 = setTimeout(() => {
      setShowSkip(false);
      onAnimationEnd();
    }, CONSTANTS.FACE_OFF_DURATION);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
    };
  }, [onAnimationEnd]);

  const handleSkip = () => {
    setShowSkip(false);
    onSkip();
  };

  // Determine round result text and color
  let roundResultText, roundResultColor;
  if (result === 'player') {
    roundResultText = 'WIN';
    roundResultColor = 'text-green-400';
  } else if (result === 'opponent') {
    roundResultText = 'LOSS';
    roundResultColor = 'text-red-400';
  } else if (result === 'double_tie') {
    roundResultText = 'DRAW';
    roundResultColor = 'text-yellow-400';
  } else {
    roundResultText = 'DRAW';
    roundResultColor = 'text-gray-400';
  }

  let resultText;
  if (result === 'player') resultText = 'VICTORY!';
  else if (result === 'opponent') resultText = 'DEFEAT!';
  else resultText = 'TIE!'; // for double_tie

  const resultColor = result === 'player' ? 'text-green-400' : result === 'opponent' ? 'text-red-400' : 'text-yellow-400';

  return (
    <motion.div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-40"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Skip Button */}
      {showSkip && (
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 bg-gray-700/80 hover:bg-gray-600/80 text-white px-4 py-2 rounded-lg text-sm font-semibold z-50 transition-colors"
        >
          Skip Animation
        </button>
      )}

      {/* Screen Shake Effect */}
      <motion.div
        className="relative w-full h-full flex items-center justify-center"
        animate={phase === 'clashing' ? { x: [0, -2, 2, -2, 2, 0] } : {}}
        transition={{ duration: 0.3 }}
      >
        {/* Player Card */}
        <motion.div
          className="absolute"
          initial={{ x: '-150%', y: '50%', scale: 0.8 }}
          animate={{ 
            x: phase === 'moving' ? '-50%' : '-60%', 
            y: 0, 
            scale: phase === 'moving' ? 1.2 : 1.3 
          }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
          style={{
            filter: phase === 'counting' || phase === 'clashing' ? 
              `drop-shadow(0 0 20px ${getElementGlow(dragonData1.element)}) drop-shadow(0 0 40px ${getElementGlow(dragonData1.element)})` : 
              `drop-shadow(0 0 10px ${getElementGlow(dragonData1.element)})`
          }}
        >
          <BattleDragonCard dragon={playerDragon} isRevealed={true} />
        </motion.div>

        {/* Opponent Card */}
        <motion.div
          className="absolute"
          initial={{ x: '150%', y: '-50%', scale: 0.8 }}
          animate={{ 
            x: phase === 'moving' ? '50%' : '60%', 
            y: 0, 
            scale: phase === 'moving' ? 1.2 : 1.3 
          }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
          style={{
            filter: phase === 'counting' || phase === 'clashing' ? 
              `drop-shadow(0 0 20px ${getElementGlow(dragonData2.element)}) drop-shadow(0 0 40px ${getElementGlow(dragonData2.element)})` : 
              `drop-shadow(0 0 10px ${getElementGlow(dragonData2.element)})`
          }}
        >
          <BattleDragonCard dragon={opponentDragon} isRevealed={true} />
        </motion.div>

        {/* VS Text */}
        {phase === 'moving' && (
          <motion.div
            className="absolute text-6xl font-black text-white/80"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.5, opacity: [0, 1, 0] }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            VS
          </motion.div>
        )}

        {/* Stat Display */}
        {(phase === 'counting' || phase === 'clashing' || phase === 'overlay' || phase === 'result') && (
          <motion.div
            className="absolute flex flex-col items-center"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="text-3xl font-mono bg-black/50 px-6 py-2 rounded-md mb-4 border border-white/20">
              {CONSTANTS.BATTLE_STAT_ICONS[stat]} {stat.toUpperCase()}
            </div>
            <div className="flex items-center space-x-6 text-5xl font-bold">
              <span className="text-blue-400">
                {phase === 'counting' ? 
                  <CountingNumber targetValue={playerStat} duration={CONSTANTS.FACE_OFF_STAT_COUNT_DURATION} /> : 
                  playerStat
                }
              </span>
              <span className="text-gray-400 text-3xl">vs</span>
              <span className="text-red-400">
                {phase === 'counting' ? 
                  <CountingNumber targetValue={opponentStat} duration={CONSTANTS.FACE_OFF_STAT_COUNT_DURATION} /> : 
                  opponentStat
                }
              </span>
            </div>
          </motion.div>
        )}

        {/* Round Result Overlay - WIN/LOSS/DRAW */}
        {phase === 'overlay' && (
          <motion.div
            className="absolute top-1/4 text-center z-10"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 10 }}
          >
            <motion.div
              className={`text-6xl font-mono font-black text-glow ${roundResultColor}`}
              animate={{ scale: result === 'player' ? [1, 1.2, 1] : 1 }}
              transition={{ duration: 0.5, repeat: result === 'player' ? 2 : 0 }}
            >
              {roundResultText}
            </motion.div>
          </motion.div>
        )}

        {/* Clash Flash Effect */}
        {phase === 'clashing' && (
          <motion.div
            className="absolute inset-0 bg-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 0.2 }}
          />
        )}

        {/* Result Text */}
        {phase === 'result' && (
          <motion.div
            className="absolute text-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 10 }}
          >
            <div className={`text-8xl font-mono font-black text-glow ${resultColor}`}>
              {resultText}
            </div>
            {tieBreaker === 'total_stats' && (
              <div className="mt-2">
                <p className="text-lg text-yellow-400 font-mono">Won on Total Power</p>
                <div className="flex items-center justify-center space-x-4 text-2xl font-bold">
                  <span className="text-blue-400">{playerTotalStats}</span>
                  <span className="text-gray-400 text-lg">vs</span>
                  <span className="text-red-400">{opponentTotalStats}</span>
                </div>
              </div>
            )}
            {result === 'double_tie' && (
              <div className="mt-2">
                <p className="text-lg text-yellow-400 font-mono">Double Tie!</p>
                <p className="text-md text-gray-300">Both players gain a point.</p>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

const BattleInterface = ({ battle, onPlayerAction, onPlayerCounter, onFaceOffEnd }) => {
  const [selectedPlayerDragonId, setSelectedPlayerDragonId] = useState(null);

  const handlePlayerDragonSelect = (dragonId) => {
    if (battle.turn === 'player') {
      setSelectedPlayerDragonId(dragonId);
    } else if (battle.turn === 'opponent' && battle.opponentAction) {
      onPlayerCounter(dragonId);
    }
  };

  const handleStatSelect = (stat) => {
    if (selectedPlayerDragonId) {
      onPlayerAction(selectedPlayerDragonId, stat);
      setSelectedPlayerDragonId(null);
    }
  };

  const handleSkipAnimation = () => {
    onFaceOffEnd();
  };

  const isPlayerTurn = battle.turn === 'player';

  return (
    <div className="h-full flex flex-col text-white p-4 relative">
      <AnimatePresence>
        {battle.phase === 'face-off' && battle.faceOff && (
          <FaceOffAnimation 
            faceOffData={battle.faceOff} 
            onAnimationEnd={onFaceOffEnd}
            onSkip={handleSkipAnimation}
          />
        )}
      </AnimatePresence>

      {/* Opponent Zone */}
      <div className="flex-1 flex flex-col items-center justify-center relative mb-4">
        <div className="flex items-center absolute top-0 z-10 bg-black/30 px-4 py-1 rounded-full">
          <img src={ASSETS.competitors[battle.opponent.avatar].path} alt="Opponent" className="w-8 h-8 rounded-full mr-2 border-2 border-red-500" />
          <span className="font-mono text-sm">{battle.opponent.name}</span>
        </div>
        <div className="flex space-x-2">
          {battle.opponentTeam.map(d => (
            <BattleDragonCard key={d.id} dragon={d} isOpponent={true} isUsed={d.used} isRevealed={d.revealed} />
          ))}
        </div>
      </div>

      {/* HUD */}
      <div className="flex-shrink-0 my-2 flex justify-between items-center bg-black/30 p-2 rounded-lg">
        <div className="w-1/3 text-left">
          <h3 className="font-mono text-lg">Round {battle.round} / {CONSTANTS.BATTLE_ROUNDS}</h3>
        </div>
        <div className="w-1/3 text-center">
          <h2 className="font-mono text-2xl">
            <span className="text-blue-400">{battle.playerScore}</span> - <span className="text-red-400">{battle.opponentScore}</span>
          </h2>
          {/* Opponent Action Display */}
          {battle.turn === 'opponent' && battle.opponentAction && (
            <div className="text-lg font-bold text-yellow-400 mt-1">
              Opponent chose {battle.opponentAction.stat.toUpperCase()}!
            </div>
          )}
        </div>
        <div className="w-1/3 text-right text-xs h-16 overflow-y-auto custom-scrollbar pr-2">
          {battle.log.slice().reverse().map((entry, i) => <p key={i}>{entry}</p>)}
        </div>
      </div>

      {/* Player Zone */}
      <div className="flex-1 flex flex-col items-center justify-center relative mt-4">
        <AnimatePresence>
          {selectedPlayerDragonId && isPlayerTurn && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-full mb-4 bg-black/50 p-2 rounded-lg flex space-x-2 z-10"
            >
              {Object.keys(CONSTANTS.DRAGONS['Fire'].stats).map(stat => (
                <button 
                  key={stat} 
                  onClick={() => handleStatSelect(stat)} 
                  className="btn btn-secondary px-3 py-1 text-xs flex items-center space-x-1"
                >
                  <span>{CONSTANTS.BATTLE_STAT_ICONS[stat]}</span>
                  <span>{stat.substring(0,3).toUpperCase()}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex space-x-2">
          {battle.playerTeam.map(d => (
            <BattleDragonCard
              key={d.id}
              dragon={d}
              isOpponent={false}
              isUsed={d.used}
              isRevealed={true}
              isSelected={selectedPlayerDragonId === d.id}
              onClick={() => !d.used && handlePlayerDragonSelect(d.id)}
            />
          ))}
        </div>
        <div className="flex items-center absolute bottom-0 z-10 bg-black/30 px-4 py-1 rounded-full">
          <img src={ASSETS.player.avatar.path} alt="Player" className="w-8 h-8 rounded-full mr-2 border-2 border-blue-500" />
          <span className="font-mono text-sm">Player</span>
        </div>
      </div>
    </div>
  );
};

export default BattleInterface;
